import { toInt } from "./utils.js";
import { cart } from "./cart-controller.js";
import {
  CART_EVENTS,
  UI_EVENTS,
  dispatchCartEvent,
  onCartEvent,
} from "./cart-events.js";

const SELECTORS = {
  state: "[data-product-license-state]",
  form: "[data-product-license-form]",
  termsCheckbox: "[data-product-license-terms]",
  addButton: "[data-product-license-add]",
  spinner: "[data-product-license-spinner]",
  cartOpenButton: "[data-product-license-cart-open]",
  errorMsg: "[data-product-license-error]",
};

class ProductLicense extends HTMLElement {
  #callbackFunctions = [];
  #isProcessing = false;
  #currentState = null;

  connectedCallback() {
    this.init().catch((error) => console.error("[ProductLicense:init]", error));
  }

  async init() {
    this.#getDataAttributes();
    this.#validateDataAttributes();
    this.#cacheStateContainers();
    this.#bindEvents();

    const onCartChange = () => this.#syncFromCart();
    this.#callbackFunctions.push(
      onCartEvent(CART_EVENTS.UPDATED, onCartChange),
      onCartEvent(CART_EVENTS.ITEM_ADDED, onCartChange),
      onCartEvent(CART_EVENTS.ITEM_CHANGED, onCartChange),
      onCartEvent(CART_EVENTS.ITEM_REMOVED, onCartChange),
    );

    await cart.ready();
    if (!this.isConnected) return;

    this.#syncFromCart();
  }

  #getDataAttributes() {
    const dataSet = this.dataset;
    this.sourceType = dataSet.productLicenseSourceType === "freebook" ? "freebook" : "product";
    this.sourceId = toInt(dataSet.productLicenseSourceId);
    this.sourceHandle = dataSet.productLicenseSourceHandle || "";
    this.sourceLabel = dataSet.productLicenseSourceLabel || "";
    this.sourcePropertyKey =
      this.sourceType === "freebook" ? "_source_freebook_id" : "_source_product_id";
    this.sourceHandlePropertyKey =
      this.sourceType === "freebook" ? "_source_freebook_handle" : "_source_product_handle";
    this.licenseVariantId = toInt(dataSet.productLicenseLicenseVariantId);
    this.termsVersion = dataSet.productLicenseTermsVersion || "";
    this.defaultState = dataSet.productLicenseDefaultState || "no_license";
    this.alreadyPurchased = dataSet.productLicenseAlreadyPurchased === "true";
    this.activeLicenseGid = dataSet.productLicenseActiveLicenseGid || "";
    this.activeLicenseValidUntil =
      dataSet.productLicenseActiveLicenseValidUntil || "";
  }

  #validateDataAttributes() {
    if (!Number.isInteger(this.sourceId) || this.sourceId <= 0) {
      throw new Error(
        `[ProductLicense] Invalid source ID for ${this.sourceType}: ${this.sourceId}`,
      );
    }
    if (
      !Number.isInteger(this.licenseVariantId) ||
      this.licenseVariantId <= 0
    ) {
      throw new Error(
        `[ProductLicense] Invalid license variant ID: ${this.licenseVariantId}`,
      );
    }
  }

  #cacheStateContainers() {
    this.stateContainers = Array.from(this.querySelectorAll(SELECTORS.state));
  }

  #bindEvents() {
    this.addEventListener("change", this.#onChange.bind(this));
    this.addEventListener("click", this.#onClick.bind(this));
  }

  #onChange(event) {
    const checkbox = event.target.closest(SELECTORS.termsCheckbox);
    if (!checkbox) return;
    this.#syncFormButton(checkbox.closest(SELECTORS.form));
  }

  #onClick(event) {
    const addButton = event.target.closest(SELECTORS.addButton);
    if (addButton) {
      this.#onAddClick(event, addButton);
      return;
    }
    if (event.target.closest(SELECTORS.cartOpenButton)) {
      this.#onCartOpenClick(event);
    }
  }

  #onCartOpenClick(event) {
    event.preventDefault();
    dispatchCartEvent(UI_EVENTS.CART_OPEN_REQUESTED, {
      source: "product-license",
    });
  }

  #syncAllForms() {
    for (const form of this.querySelectorAll(SELECTORS.form)) {
      this.#syncFormButton(form);
    }
  }

  #syncFormButton(form) {
    if (!form) return;
    const checkbox = form.querySelector(SELECTORS.termsCheckbox);
    const addButton = form.querySelector(SELECTORS.addButton);
    if (!checkbox || !addButton) return;
    const accepted = checkbox.checked === true;
    addButton.disabled = !accepted;
    addButton.setAttribute("aria-disabled", accepted ? "false" : "true");
  }

  #syncFromCart() {
    const cartState = cart.state;
    if (this.#cartHasLicenseFor(cartState)) {
      this.#showState("in_cart");
      return;
    }
    if (
      this.defaultState === "logged_out" ||
      this.defaultState === "active" ||
      this.defaultState === "expired"
    ) {
      this.#showState(this.defaultState);
      return;
    }
    if (this.sourceType === "freebook") {
      this.#showState("no_license");
      return;
    }
    const sourceInCart = this.#cartHasSourceProduct(cartState);
    if (this.alreadyPurchased || sourceInCart) {
      this.#showState("no_license");
    } else {
      this.#showState("requires_purchase");
    }
  }

  #showState(state) {
    this.#currentState = state;
    for (const container of this.stateContainers) {
      const matches = container.dataset.productLicenseState === state;
      container.toggleAttribute("hidden", !matches);
    }
    this.#syncAllForms();
  }

  #cartHasLicenseFor(cartState) {
    if (!cartState || !Array.isArray(cartState.items)) return false;
    return cartState.items.some(
      (item) =>
        toInt(item.properties?.[this.sourcePropertyKey]) === this.sourceId,
    );
  }

  #cartHasSourceProduct(cartState) {
    if (this.sourceType !== "product") return false;
    if (!cartState || !Array.isArray(cartState.items)) return false;
    return cartState.items.some((item) => item.product_id === this.sourceId);
  }

  #onAddClick(event, addButton) {
    event.preventDefault();
    if (this.#isProcessing) return;
    const form = addButton.closest(SELECTORS.form);
    const checkbox = form?.querySelector(SELECTORS.termsCheckbox);
    if (!checkbox?.checked) return;
    void this.#addLicenseToCart(form, addButton).catch((error) =>
      console.error("[ProductLicense:addLicenseToCart]", error),
    );
  }

  // Additive-Kontingente-Modell: jeder Kauf ist eine eigenstaendige Lizenz
  // (50 Teile, 1 Jahr ab Kaufdatum). Kein _renewal_of, kein
  // _valid_until_after_purchase — die Laufzeit rechnet license-create aus
  // dem Bestelldatum.
  async #addLicenseToCart(form, addButton) {
    this.#isProcessing = true;
    this.#setProcessingUI(form, addButton, true);
    this.#hideError();
    try {
      const properties = {
        "Lizenz für": this.sourceLabel,
        [this.sourcePropertyKey]: String(this.sourceId),
        [this.sourceHandlePropertyKey]: this.sourceHandle,
        _terms_accepted_at: new Date().toISOString(),
      };
      if (this.termsVersion) {
        properties._terms_version = this.termsVersion;
      }
      await cart.add({
        id: this.licenseVariantId,
        quantity: 1,
        properties,
      });
    } catch (error) {
      this.#showError(String(error?.message || error));
      throw error;
    } finally {
      this.#isProcessing = false;
      this.#setProcessingUI(form, addButton, false);
    }
  }

  #setProcessingUI(form, addButton, isProcessing) {
    if (!addButton) return;
    const checkbox = form?.querySelector(SELECTORS.termsCheckbox);
    addButton.setAttribute("aria-busy", isProcessing ? "true" : "false");
    addButton.disabled = isProcessing || checkbox?.checked !== true;
    const spinner = addButton.querySelector(SELECTORS.spinner);
    spinner?.toggleAttribute("hidden", !isProcessing);
  }

  #showError(message) {
    const errorMsg = this.querySelector(SELECTORS.errorMsg);
    if (!errorMsg) return;
    errorMsg.textContent = message;
    errorMsg.removeAttribute("hidden");
  }

  #hideError() {
    const errorMsg = this.querySelector(SELECTORS.errorMsg);
    if (!errorMsg) return;
    errorMsg.textContent = "";
    errorMsg.setAttribute("hidden", "");
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    this.#callbackFunctions.forEach((fn) => fn());
    this.#callbackFunctions = [];
  }
}

if (!customElements.get("product-license")) {
  customElements.define("product-license", ProductLicense);
}
